import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Spinner } from '@1trading/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { getErrorMessage } from '@/lib/api/error';
import { manualAssignParkedDeposit } from '@/services/api/parked-deposits';

interface ManualAssignDialogProps {
  transactionId: string;
  /** Assign succeeded — the page closes this dialog and refetches the list */
  onCompleted: () => void;
  onClose: () => void;
}

export const ManualAssignDialog = ({ transactionId, onCompleted, onClose }: ManualAssignDialogProps) => {
  const [payoutAccountId, setPayoutAccountId] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    // Deliberate fix: legacy silently no-oped on a blank id after the
    // justification dialog closed — validate BEFORE opening it instead.
    const trimmedId = payoutAccountId.trim();
    if (!trimmedId) {
      setFieldError('Payout Account ID is required');
      return;
    }
    setFieldError(null);
    // Collect the justification first, then run the request with this dialog still
    // open — API errors land inline on the field so the operator can correct the id
    // (legacy read `result.error` from a masked 200, in React it's a real
    // 400/500 whose body.error becomes the thrown message).
    const justification = await confirmAction({
      title: 'Confirm Manual Assign Parked Deposit',
      description: 'Proceeding with this action will manually assign the selected parked deposit. Your action here will be logged and will be auditable.',
      actionLabel: 'Manual Assign Parked Deposit',
    });
    if (justification === null) return;
    setIsSubmitting(true);
    try {
      await manualAssignParkedDeposit({ transactionId, payoutAccountId: trimmedId, comment: justification });
      toast.success(`Parked deposit ${transactionId} manually assigned`);
      onCompleted();
    } catch (error) {
      setFieldError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : handleClose())} open>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manual Assign Transaction</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="payout-account-id">Payout Account ID</Label>
          <Input
            aria-invalid={fieldError ? true : undefined}
            id="payout-account-id"
            onChange={(event) => {
              setPayoutAccountId(event.target.value);
              setFieldError(null);
            }}
            value={payoutAccountId}
          />
          {fieldError ? <p className="text-sm text-red-500">{fieldError}</p> : null}
        </div>
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={handleClose} type="button" variant="destructive">
            Cancel
          </Button>
          <Button className="gap-2" disabled={isSubmitting} onClick={handleSave} type="button" variant="primary">
            {isSubmitting ? <Spinner className="size-4" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
ManualAssignDialog.displayName = 'ManualAssignDialog';
